import { getSessionToken } from './sessionToken';
import axios from 'axios';
import { X_CLIENT, isOwnApiRequest } from './clientIdentity';
import { notifyError } from '@/api/notifyError';
import { isWebView, postToNative } from '@/utils/webview';
import { safeJsonParse } from '@/utils/safeJson';
import { parseApiError } from '@/services/errors';
import { syncServerClockFromHeaders } from '@/utils/serverClock';
import { handleInactiveUser401 } from '@/api/authInactive';
import { isPublicTokenRequestUrl } from '@/api/publicTokenRequestRule';

// 配置API基础路径
if (!import.meta.env.VITE_API_BASE) {
  console.error('[config] VITE_API_BASE is not set — API calls will fail');
}
export const API_BASE = import.meta.env.VITE_API_BASE ?? '';
// VITE_API_BASE already includes /api/v1 (e.g. https://api-dev.novavacation.com/api/v1)
export const AUTH_API_BASE = API_BASE;

// 接口响应类型定义
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  code?: number;
}

// 配置axios实例
axios.defaults.baseURL = API_BASE;
axios.defaults.timeout = parseInt(import.meta.env.VITE_API_TIMEOUT || '300000', 10);
axios.defaults.headers.common['Content-Type'] = 'application/json';

// 覆盖默认的 JSON 反序列化，使用 safeJsonParse 防止 Snowflake ID 等大整数精度丢失
axios.defaults.transformResponse = [
  (data: string) => {
    if (typeof data === 'string') {
      try {
        return safeJsonParse(data);
      } catch {
        return data;
      }
    }
    return data;
  }
];

// 添加请求拦截器
axios.interceptors.request.use(
  async (config) => {
    // 客户端标识（旧 AI 接口调用量统计用，见 clientIdentity.ts）：只给发往本仓 API 的请求加
    if (isOwnApiRequest(config.url, config.baseURL, API_BASE)) {
      config.headers['X-Client'] = X_CLIENT;
    }
    // MCP 附件上传公开端点：token 即授权，压根不需要也不该带任何登录态头。
    // 必须在 Clerk 分支之前短路——不然的话：ClerkAuthProvider 的 useEffect（把
    // getToken 挂到 window.__getClerkToken）比 React.lazy 页面组件的 mount 早，
    // 所以匿名访客首次打开 /upload/:token 时 window.__getClerkToken 已经指向
    // 真实的 Clerk getToken；而这台环境用 pk_live_ 生产 key 在 localhost 域名
    // 下会被 Clerk 判定域名不合法直接 400，getToken() 返回的 Promise 永久不
    // resolve/reject。AuthContext 有一个 8s 兜底会清掉 window.__getClerkToken
    // （防止"以后"的请求卡住，见 AuthContext.tsx 附近注释），但清不掉**已经
    // 挂起**的这次 await——页面会卡死在 Loading 态，不是被踢 401，是永久挂起。
    // 见 docs/superpowers/sdd/2026-08-28-mcp-attachments-web/task-6-report.md。
    // 外部承包商工单页 /job/:token 的公开端点同理（判定见 publicTokenRequestRule.ts）。
    if (isPublicTokenRequestUrl(config.url)) {
      return config;
    }

    // Refresh authenticates with its body credential; avoid recursive token lookup.
    if (config.url?.includes('/system/auth/token/refresh')) return config;
    const token = await getSessionToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    else delete config.headers.Authorization;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 添加响应拦截器
axios.interceptors.response.use(
  (response) => {
    syncServerClockFromHeaders(response?.headers as any);
    // 安全检查：确保 response 和 response.data 存在
    if (!response || !response.data) {
      return response?.data || response;
    }
    
    // 检查是否是包含 status_code 的错误响应（422, 400 等验证错误）
    // 只有当 status_code 表示错误时才 reject（>= 400），避免误判成功响应
    if (response.data.hasOwnProperty('status_code') && response.data.status_code >= 400) {
      return Promise.reject({ response });
    }

    // 检查是否包含 error 字段的错误响应
    // 只有当 error 字段有实际错误值时才 reject（排除 null/undefined/空字符串）
    if (response.data.error && response.data.hasOwnProperty('message')) {
      return Promise.reject({ response });
    }

    // 检查返回码，只有当返回码为成功码时才执行正常流程，否则弹出toast提示
    // 注意：只检查数字类型的 code，避免将业务字段（如 ServiceCategory.code）误判为响应码
    // 成功码包括：0（通用成功）、200、201
    if (response.data.hasOwnProperty('code') &&
        typeof response.data.code === 'number' &&
        response.data.code !== 0 &&
        response.data.code !== 200 &&
        response.data.code !== 201) {
      // 非成功状态码，显示错误信息
      notifyError(response.data.msg || 'Error');
      return Promise.reject(response.data);
    }
    
    // 正常返回数据
    // 如果响应有包装结构 { code: 200, data: {...} }，则返回 data 字段
    // 否则直接返回 response.data（如登录接口直接返回 { access_token, refresh_token }）
    if (response.data.hasOwnProperty('data')) {
      return response.data.data;
    }
    return response.data;
  },
  async (error) => {
    syncServerClockFromHeaders(error.response?.headers as any);
    // 翻译后端 typed-error envelope（{ error / error_code, message, data }）为 typed Error 子类。
    // 注：401 走自动刷新分支，需要保留原始 axios error 的 error.config，因此 401 不在此翻译。
    // 详见 src/services/errors.ts 与 Spec §6.2.3。
    const translateTypedError = (): Error | null => {
      const data = error?.response?.data;
      if (!data) return null;
      if (!data.error && !data.error_code) return null;
      return parseApiError(error.response.status, data);
    };

    // 400 和 422 是验证错误，由调用方处理，不在这里显示 toast
    if (error?.response && (error.response.status === 400 || error.response.status === 422)) {
      const typed = translateTypedError();
      return Promise.reject(typed ?? error);
    }

    // 处理 404 错误，统一显示错误信息给用户
    if (error?.response && error.response.status === 404) {
      // 调用方通用 opt-out：请求 config 里带 suppressNotFoundToast: true 的探测型
      // 请求（如 reservation guest-form GET——无登记表的预订必然 404，是正常业务态）
      // 不弹全局 toast，直接 reject 原始 axios error（保留 .response.status 供调用方
      // 判 404）。默认 undefined = 行为零变化。下面三个硬编码 URL 豁免是历史存量，
      // 新的"404 是正常态"的调用一律用这个开关，别再往正则清单里堆。
      // 用法：axios.get(url, { suppressNotFoundToast: true } as any)
      if ((error.config as any)?.suppressNotFoundToast) {
        return Promise.reject(error);
      }
      // thermostat GET 404 由调用方优雅处理（无温控器是正常状态），不弹 toast
      const isThermostatLookup =
        error.config?.method?.toLowerCase() === 'get' &&
        /\/smart-locks\/devices\/.*\/thermostat/.test(error.config?.url ?? '');
      // MCP 上传落地页：token 不存在/已过期时 GET session、POST presign、POST commit
      // 三个公开端点都必 404，页面自己渲染"链接已失效"卡片。公开页上再飘一条全局
      // toast 只是重复噪音，而且匿名访客没有任何上下文能理解它。
      // 注意：不按 method 收窄——上传中途（presign/commit 是 POST）session 过期
      // 同样要静默，之前只放过 GET 导致上传中途的 404 仍然弹全局 toast
      // （规则只按 URL 形状收紧，见 isUploadSessionRequestUrl 的注释）。
      // 外部承包商工单页 /job/:token 的公开端点同理（publicTokenRequestRule.ts）。
      const isUploadSessionLookup = isPublicTokenRequestUrl(error.config?.url);
      if (!isThermostatLookup && !isUploadSessionLookup) {
        const errorData = error.response?.data;
        // 检查是否有 message 字段，优先显示 message，否则显示 error 字段
        const errorMessage = errorData?.message || errorData?.error || errorData?.msg || 'Resource not found';
        notifyError(errorMessage);
      }
      const typed = translateTypedError();
      return Promise.reject(typed ?? error);
    }

    // 处理 403 / 409 / 423 等业务错误（typed-error envelope 主战场）
    if (
      error?.response &&
      (error.response.status === 403 ||
        error.response.status === 409 ||
        error.response.status === 423)
    ) {
      const typed = translateTypedError();
      if (typed) return Promise.reject(typed);
    }
    
    if (error.response && error.response.status === 401) {
      // 公开 token 页（/upload/:token、/job/:token）的请求根本没带登录头：它的 401 与员工
      // 登录态无关，不能走下面的「刷新 / 清 token / 登出」—— 否则承包商页一个 401 会把
      // 同一浏览器里员工的登录态清掉。原样交给页面自己处理。
      if (isPublicTokenRequestUrl(error.config?.url)) {
        return Promise.reject(error);
      }

      // 账号被停用是终态：Clerk session 仍健康、reload 永远成功，任何刷新/
      // 重试都只会再拿一个 401（僵尸循环）。直接统一登出。
      if (handleInactiveUser401(error)) {
        return Promise.reject(error);
      }

      // 处理未授权错误
      const isLoginRequest = error.config?.url?.includes('/system/auth/login');
      const isVerifyUserRequest = error.config?.url?.includes('/system/user/verify-user');
      // refresh 端点自身的 401 必须直接拒绝：否则下方 WebView 分支会再次调用
      // authApi.refreshToken()（每次都是新 config、_retry 不生效）→ 无限递归。
      // refresh 401 = refresh_token 已失效（轮换重放/登出/过期），由调用方
      // （authApi.refreshToken 的 catch）清 token 收场。
      const isRefreshRequest = error.config?.url?.includes('/system/auth/token/refresh');

      // 如果是登录/验证用户/刷新 token 请求，直接拒绝（不重试）
      if (isLoginRequest || isVerifyUserRequest || isRefreshRequest) {
        return Promise.reject(error);
      }

      // Internal login: browser and WebView share the same refresh owner.
      if ((isWebView() || (!(window as any).Clerk?.session && !window.__getClerkToken)) && error.config && !error.config._retry) {
        error.config._retry = true;

        // 优先使用 refresh_token 自主刷新（ticket 模式下 WebApp 有自己的 refresh_token）
        const refreshToken = localStorage.getItem('REFRESH_TOKEN');
        if (refreshToken) {
          try {
            const newAccessToken = await getSessionToken(true);
            // refreshToken() 已更新 localStorage，用新 token 重试
            error.config.headers.Authorization = `Bearer ${newAccessToken}`;
            return axios(error.config);
          } catch (refreshError) {
            console.error('[WebView] Refresh token failed:', refreshError);
            localStorage.removeItem('ACCESS_TOKEN');
            localStorage.removeItem('REFRESH_TOKEN');
          }
        }

        // Fallback: 通知 APP 刷新（兼容旧的 localStorage 注入模式）
        if ((window as any).ReactNativeWebView) {
          return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              cleanup();
              reject(error);
            }, 10000); // 10 秒超时

            const handler = () => {
              cleanup();
              // App 已刷新 token 到 localStorage，重试原始请求
              resolve(axios(error.config));
            };

            const cleanup = () => {
              clearTimeout(timeout);
              window.removeEventListener('tokenInjected', handler);
            };

            window.addEventListener('tokenInjected', handler);
            postToNative('TOKEN_REFRESH_REQUEST', { reason: '401' });
          });
        }
      }

      // 检查是否使用 Clerk token
      // @ts-ignore
      const hasClerkSession = window.Clerk?.session;

      if (hasClerkSession && error.config && !error.config._retry) {
        error.config._retry = true;

        try {
          // Clerk 会自动刷新 token，我们只需要重新获取即可
          // @ts-ignore
          await getSessionToken(true);


          // 重试原始请求（拦截器会自动附加新 token）
          return axios(error.config);
        } catch (refreshError) {
          console.error('❌ Clerk session reload failed:', refreshError);

          // Clerk session 失效，触发统一的退出流程
          // 不直接调用 Clerk.signOut() 和 localStorage.clear()
          // 而是发送事件，让 useAuth hook 处理统一的退出逻辑
          console.log('🚨 触发 auth:unauthorized 事件（Clerk session 失效）');
          window.dispatchEvent(new CustomEvent('auth:unauthorized', {
            detail: {
              error: error.response?.data,
              status: 401,
              reason: 'clerk_session_reload_failed'
            }
          }));

          return Promise.reject(error);
        }
      }

      // 如果没有 Clerk session，清除旧 token 并跳转登录
      localStorage.removeItem('ACCESS_TOKEN');
      localStorage.removeItem('REFRESH_TOKEN');

      // 发送自定义事件通知应用进行登出和跳转
      window.dispatchEvent(new CustomEvent('auth:unauthorized', {
        detail: { error: error.response?.data, status: 401 }
      }));
    }
    return Promise.reject(error);
  }
);

// 导出axios实例以便直接使用
export default axios;